// V2 "scene" maps: AI-authored, single-image maps. A scene map is ONE flat scene
// PNG + a painted collision grid + self-contained doors/spawn — no 256px tile
// grid and no atari hitmap. Legacy tile maps (mapGraph.ts / maps.json) are
// untouched and coexist; the renderer dispatches on which registry a map id is in.
//
// Authoring is the whole point: generate the scene art, open ?mapedit=1, paint
// collision + drop doors/props, export this JSON into ./maps/<id>.json, add the
// PNG under assets/maps/<id>/, done. No backend change (the world server treats
// mapId as an opaque room key), no manifest regen — the glob below picks it up.

import type { Facing } from "@ygoevo/world-protocol";
import type { SpawnPoint } from "./mapGraph.ts";

/** Painted walkability, 1 bit per author cell (row-major, 1 = blocked). The
 *  renderer expands this into the engine's native 4px collision grid on load, so
 *  movement / A* / click-to-walk are identical to legacy maps. */
export interface SceneCollision {
  /** Author cell size in world px (paint resolution, e.g. 16). */
  cell: number;
  cols: number;
  rows: number;
  /** base64 of the packed bitmap (cols*rows bits). */
  bits: string;
}

/** A self-contained door. `rect` (world-px [x0,y0,x1,y1]) is the trigger when
 *  present; otherwise it's a circle of `radius` around (x,y). The editor always
 *  writes a rect, so scene doors need no atari-zone binding. */
export interface ScenePortal {
  to: string;
  label: string;
  x: number;
  y: number;
  radius?: number;
  rect?: [number, number, number, number];
}

/** A foreground prop the player can walk behind. `midground` = Y-sorted by its
 *  bottom edge (occludes correctly); `overlay` = always-on-top (walk-under tops
 *  like an archway/bridge). Transparent PNGs, so occlusion is pixel-perfect. */
export interface SceneObject {
  /** Top-left in world px. */
  x: number;
  y: number;
  src: string;
  layer?: "midground" | "overlay";
}

export interface SceneMap {
  id: string;
  /** HUD area name. */
  title: string;
  /** Asset path to the single scene PNG. */
  scene: string;
  width: number;
  height: number;
  spawn: SpawnPoint;
  portals: ScenePortal[];
  collision: SceneCollision;
  objects?: SceneObject[];
}

// Eagerly load every authored scene map. They're tiny JSON (a scene map's whole
// collision packs to a few KB), so eager import keeps lookups synchronous like
// the legacy portal/spawn registries. Zero matches (none authored yet) is fine.
const modules = import.meta.glob<{ default: SceneMap }>("./maps/*.json", { eager: true });
const REGISTRY = new Map<string, SceneMap>();
for (const m of Object.values(modules)) REGISTRY.set(m.default.id, m.default);

export function sceneMap(id: string): SceneMap | undefined {
  return REGISTRY.get(id);
}
export function isSceneMap(id: string): boolean {
  return REGISTRY.has(id);
}
export function allSceneMaps(): SceneMap[] {
  return [...REGISTRY.values()];
}

// ── bit packing (shared by the loader AND the ?mapedit editor) ────────────────
/** Pack a 0/1 cell array into base64 (1 bit/cell, LSB-first within each byte). */
export function packBits(cells: Uint8Array): string {
  const bytes = new Uint8Array((cells.length + 7) >> 3);
  for (let i = 0; i < cells.length; i++) if (cells[i]) bytes[i >> 3]! |= 1 << (i & 7);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
/** Inverse of packBits. `count` = cols*rows (bytes may hold a few trailing pad bits). */
export function unpackBits(b64: string, count: number): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(count);
  for (let i = 0; i < count; i++) out[i] = (bin.charCodeAt(i >> 3) >> (i & 7)) & 1;
  return out;
}

export type { Facing };
